import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar
} from '@carbon/react';

import { routes } from 'core/routing';
import { useHasAccess } from 'core/user/role';
import SiteProjectsToolbarCell from 'features/all-projects/components/SiteProjectsToolbarCell';
import useSiteProjectsController from 'features/all-projects/useSiteProjectsController';
import useProjectsQuery from 'shared/api/projects/useProjectsQuery';
import TableToolbarCell from 'shared/components/data-table/TableToolbarCell';
import Link from 'shared/components/Link';
import OverlayLoader from 'shared/components/OverlayLoader';
import Pagination, { usePagination } from 'shared/components/Pagination';
import ProjectTier, { ProjectTierTooltip } from 'shared/components/ProjectTier';
import ProjectListTableToolbar from 'shared/project-list/ProjectListTableToolbar';
import { formatDate } from 'utils/dates';
import styles from 'theme/features/all-projects/all-projects.module.scss';

const SiteProjectsTableWrapper = ({
  rows,
  projects,
  getTableContainerProps,
  getToolbarProps,
  getTableProps,
  getSelectionProps,
  getHeaderProps,
  getRowProps,
  selectedRows,
  headers,
  selectAll,
  isLoading,
  onDuplicate,
  onArchive,
  onUnarchive,
  onConfidential,
  onUnconfidential,
  onFinal,
  onUnfinal,
  pagination
}) => {
  const { hasEditAccess } = useHasAccess();

  return (
    <TableContainer className={styles.table} {...getTableContainerProps()}>
      <OverlayLoader small={rows.length === 0} isLoading={isLoading} />
      <TableToolbar {...getToolbarProps()} size="sm">
        <ProjectListTableToolbar selectAll={selectAll} selectedRows={selectedRows} />
      </TableToolbar>
      <Table {...getTableProps()}>
        <TableHead>
          <TableRow>
            <TableSelectAll {...getSelectionProps()} />
            {headers.map((header) => (
              <TableHeader {...getHeaderProps({ header })} key={header.key}>
                {header.header}
              </TableHeader>
            ))}
            {hasEditAccess() && <TableToolbarCell.Header />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const project = projects[index];

            return (
              <TableRow {...getRowProps({ row })} key={row.id}>
                <TableSelectRow {...getSelectionProps({ row })} />
                {row.cells.map((cell) => (
                  <TableCell key={cell.id}>{cell.value}</TableCell>
                ))}
                {hasEditAccess() && (
                  <SiteProjectsToolbarCell
                    project={project}
                    onDuplicate={onDuplicate}
                    onArchive={onArchive}
                    onUnarchive={onUnarchive}
                    onConfidential={onConfidential}
                    onUnconfidential={onUnconfidential}
                    onFinal={onFinal}
                    onUnfinal={onUnfinal}
                  />
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Pagination
        page={pagination.page}
        pageSize={pagination.pageSize}
        isLastPage={pagination.isLastPage}
        onPageChange={pagination.setPage}
        onPageSizeChange={pagination.setPageSize}
      />
    </TableContainer>
  );
};

const SiteProjectsTable = ({
  site = null,
  onAllProjectsRemoved,
  isArchived = false,
  isConfidential,
  isFinal,
  title,
  description,
  searchValue = null
}) => {
  const pagination = usePagination();

  const {
    data: projects,
    isLoading: isLoadingProjects,
    isRefetching: isRefetchingProjects,
    refetch: refetchProjects
  } = useProjectsQuery({
    address: site?.address ?? undefined,
    page: pagination.page,
    pageSize: pagination.pageSize,
    unknownSite: !site,
    archived: isArchived,
    confidented: isConfidential,
    finaled: isFinal,
    ...(searchValue && { search: searchValue })
  });

  const {
    isArchiveLoading,
    isDuplicateLoading,
    isConfidentialLoading,
    isFinalLoading,
    archive,
    unarchive,
    confidential,
    unconfidential,
    final,
    unfinal,
    duplicate
  } = useSiteProjectsController({
    projects,
    refetchProjects,
    onAllProjectsRemoved
  });

  const rows =
    projects?.map((x) => ({
      id: x.id.toString(),
      name: <Link href={routes.projectOutputs(x.id)}>{x.name}</Link>,
      tier: x.tier != null && <ProjectTier tier={x.tier} isValid={x.tierIsValid} />,
      createdOn: formatDate(x.createdOn),
      createdBy: x.createdBy,
      updatedOn: x.updatedOn ? formatDate(x.updatedOn) : '-',
      updatedBy: x.updatedBy ?? '-',
      label:
        x.isFinal && x.isConfidential ? (
          <span className={styles.label}>Final Version | Confidential</span>
        ) : x.isFinal ? (
          <span className={styles.label}>Final Version</span>
        ) : x.isConfidential ? (
          <span className={styles.label}>Confidential</span>
        ) : (
          ''
        )
    })) ?? [];

  const headers = [
    { key: 'name', header: 'Project Name' },
    { key: 'label', header: '' },
    {
      key: 'tier',
      header: (
        <>
          Tier
          <span style={{ top: 0, position: 'relative' }}>
            <ProjectTierTooltip />
          </span>
        </>
      )
    },
    { key: 'createdOn', header: 'Created On' },
    { key: 'createdBy', header: 'Created By' },
    { key: 'updatedOn', header: 'Modified On' },
    { key: 'updatedBy', header: 'Modified By' }
  ];

  return (
    <DataTable
      headers={headers}
      rows={rows}
      size="sm"
      render={(props) => (
        <SiteProjectsTableWrapper
          {...props}
          title={title}
          description={description}
          projects={projects ?? []}
          headers={headers}
          isLoading={
            isLoadingProjects ||
            isRefetchingProjects ||
            isArchiveLoading ||
            isDuplicateLoading ||
            isConfidentialLoading ||
            isFinalLoading
          }
          onDuplicate={duplicate}
          onArchive={archive}
          onUnarchive={unarchive}
          onConfidential={confidential}
          onUnconfidential={unconfidential}
          onFinal={final}
          onUnfinal={unfinal}
          pagination={{
            ...pagination,
            isLastPage: !projects || projects.length < pagination.pageSize
          }}
        />
      )}
    />
  );
};

export default SiteProjectsTable;
